import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';

import { WorkflowFolderApiService } from '../services/workflow-folder-api.service';
import { AuthService } from '../../../auth-pages/services/auth.service';
import { ToastService } from '../../../../shared/services/toast.service';
import {
  WorkflowFolderResponse,
  WorkflowFolderCreateRequest,
  WorkflowFolderUpdateRequest
} from '../models/workflow.model';
import { HasPermissionDirective } from '../../../../shared/directives/has-permission.directive';
import { DisableWithoutPermissionDirective } from '../../../../shared/directives/disable-without-permission.directive';

@Component({
  selector: 'app-workflow-folder-list',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule, FormsModule, HasPermissionDirective, DisableWithoutPermissionDirective],
  templateUrl: './workflow-folder-list.component.html'
})
export class WorkflowFolderListComponent implements OnInit, OnDestroy {
  folders: WorkflowFolderResponse[] = [];
  loading = false;
  error = '';

  showCreateModal = false;
  showEditModal = false;
  showDeleteModal = false;

  editingFolder: WorkflowFolderResponse | null = null;
  deletingFolder: WorkflowFolderResponse | null = null;

  createForm!: FormGroup;
  editForm!: FormGroup;
  saving = false;
  deleteLoading = false;
  formError = '';

  private destroy$ = new Subject<void>();

  constructor(
    private folderApi: WorkflowFolderApiService,
    private authService: AuthService,
    private toast: ToastService,
    private fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.initForms();
    this.loadFolders();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private get businessId(): number {
    return this.authService.getBusinessId() ?? 0;
  }

  private initForms(): void {
    this.createForm = this.fb.group({
      name:           ['', [Validators.required, Validators.maxLength(100)]],
      description:    ['']
    });
    this.editForm = this.fb.group({
      name:           ['', [Validators.required, Validators.maxLength(100)]],
      description:    [''],
      isActive:       [true]
    });
  }

  loadFolders(): void {
    this.loading = true;
    this.folderApi.getFolders(this.businessId).pipe(takeUntil(this.destroy$)).subscribe({
      next: (folders) => { this.folders = folders; this.loading = false; },
      error: (err) => {
        this.error = err.error?.message || 'Error al cargar las carpetas.';
        this.loading = false;
      }
    });
  }

  // ── Crear ─────────────────────────────────────────────────────────────────

  openCreateModal(): void {
    this.createForm.reset({ name: '', description: '' });
    this.formError = '';
    this.showCreateModal = true;
  }

  confirmCreate(): void {
    if (this.createForm.invalid) { this.createForm.markAllAsTouched(); return; }
    this.saving = true;
    this.formError = '';
    const v = this.createForm.value;
    const request: WorkflowFolderCreateRequest = {
      intIdBussines:  this.businessId,
      name:           v.name.trim(),
      description:    v.description?.trim() || null
    };
    this.folderApi.create(request).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.toast.created('Carpeta');
        this.loadFolders();
        this.closeCreateModal();
      },
      error: (err) => {
        this.formError = err.error?.message || 'No se pudo crear la carpeta.';
        this.saving = false;
      }
    });
  }

  closeCreateModal(): void {
    this.showCreateModal = false;
    this.saving = false;
    this.formError = '';
  }

  // ── Editar ────────────────────────────────────────────────────────────────

  openEditModal(folder: WorkflowFolderResponse): void {
    this.editingFolder = folder;
    this.editForm.patchValue({
      name:        folder.name,
      description: folder.description ?? '',
      isActive:    folder.isActive
    });
    this.formError = '';
    this.showEditModal = true;
  }

  confirmEdit(): void {
    if (!this.editingFolder || this.editForm.invalid) { this.editForm.markAllAsTouched(); return; }
    this.saving = true;
    this.formError = '';
    const v = this.editForm.value;
    const request: WorkflowFolderUpdateRequest = {
      name:        v.name.trim(),
      description: v.description?.trim() || null,
      isActive:    v.isActive
    };
    this.folderApi.update(this.editingFolder.id, request).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.toast.updated('Carpeta');
        this.loadFolders();
        this.closeEditModal();
      },
      error: (err) => {
        this.formError = err.error?.message || 'No se pudo actualizar la carpeta.';
        this.saving = false;
      }
    });
  }

  closeEditModal(): void {
    this.showEditModal = false;
    this.editingFolder = null;
    this.saving = false;
    this.formError = '';
  }

  // ── Inactivar / Activar ───────────────────────────────────────────────────

  /** Id de la carpeta cuyo cambio de estado está en curso (deshabilita el botón). */
  togglingId: number | null = null;

  /**
   * INC_CRM_020 (2) inc9 — antes no existía: el botón "Eliminar" inactivaba por dentro,
   * así que ni la acción decía la verdad ni había forma de reactivar una carpeta.
   */
  toggleActive(folder: WorkflowFolderResponse): void {
    const activar = !folder.isActive;
    this.togglingId = folder.id;
    this.folderApi.setActive(folder.id, activar).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.togglingId = null;
        this.loadFolders();
        this.toast.success({
          title: activar ? 'Carpeta activada' : 'Carpeta inactivada',
          message: activar
            ? `"${folder.name}" vuelve a estar disponible para organizar workflows.`
            : `"${folder.name}" ya no se ofrece al organizar workflows. Ahora puedes eliminarla.`
        });
      },
      error: (err) => {
        this.togglingId = null;
        const msg = err.error?.message || 'No se pudo cambiar el estado de la carpeta.';
        this.error = msg;
        this.toast.error({ title: 'Error', message: msg });
      }
    });
  }

  // ── Eliminar ──────────────────────────────────────────────────────────────

  openDeleteModal(folder: WorkflowFolderResponse): void {
    this.deletingFolder = folder;
    this.showDeleteModal = true;
  }

  confirmDelete(): void {
    if (!this.deletingFolder) return;
    this.deleteLoading = true;
    const nombre = this.deletingFolder.name;
    this.folderApi.delete(this.deletingFolder.id).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.closeDeleteModal();
        this.loadFolders();
        this.toast.deleted('Carpeta', `Se eliminó "${nombre}" definitivamente.`);
      },
      error: (err) => {
        // El backend rechaza el borrado si la carpeta sigue activa o todavía tiene
        // contenido; su mensaje ya explica el motivo, así que se muestra tal cual.
        const msg = err.error?.message || 'No se pudo eliminar la carpeta.';
        this.error = msg;
        this.closeDeleteModal();
        this.toast.error({ title: 'No se pudo eliminar', message: msg });
      }
    });
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.deletingFolder = null;
    this.deleteLoading = false;
  }

  isFieldInvalid(form: FormGroup, field: string): boolean {
    const ctrl = form.get(field);
    return !!(ctrl && ctrl.invalid && ctrl.touched);
  }
}
